import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, PasswordInput } from '@mantine/core';
import apiService from '../../services/api';
import type { ResetPasswordProps } from '../../types';

interface ResetPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ onBackToHome }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState<ResetPasswordFormData>({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 10) {
      errors.push('Password must be at least 10 characters long');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordErrors = validatePassword(formData.newPassword);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('. '));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiService.resetPassword(token, formData.newPassword);

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      console.error('Password reset error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password. The link may have expired.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-5 bg-gradient-to-br from-[#2c3e50] to-[#34495e] font-sans">
        <div className="bg-white rounded-xl p-5 sm:p-8 md:p-10 shadow-md max-w-[400px] w-full">
          <div className="text-3xl sm:text-5xl mb-5 text-center">✓</div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-800 text-center">Password Reset Successful!</h1>
          <p className="text-base text-gray-500 mb-8 text-center">
            Your password has been reset. Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-5 bg-gradient-to-br from-[#2c3e50] to-[#34495e] font-sans">
      <div className="bg-white rounded-xl p-5 sm:p-8 md:p-10 shadow-md max-w-[400px] w-full">
        {onBackToHome && (
          <button onClick={onBackToHome} className="bg-transparent text-[#2c3e50] border-none cursor-pointer text-sm mb-5 underline">
            ← Back to Home
          </button>
        )}

        <div className="text-3xl sm:text-5xl mb-5 text-center">🔒</div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-800 text-center">Reset Your Password</h1>
        <p className="text-base text-gray-500 mb-8 text-center">
          Enter your new password below
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <PasswordInput
            placeholder="New Password"
            value={formData.newPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange({ ...e, target: { ...e.target, name: 'newPassword', value: e.target.value } })}
            required
            disabled={loading || !token}
            radius="md"
            size="md"
          />

          <PasswordInput
            placeholder="Confirm New Password"
            value={formData.confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange({ ...e, target: { ...e.target, name: 'confirmPassword', value: e.target.value } })}
            required
            disabled={loading || !token}
            radius="md"
            size="md"
          />

          <p className="text-xs text-gray-500 mt-1">
            Password must be at least 10 characters with uppercase, lowercase, and special character
          </p>

          <Button
            type="submit"
            loading={loading}
            disabled={!token}
            radius="md"
            size="lg"
            color="orange"
            className="mt-2"
          >
            Reset Password
          </Button>
        </form>

        {error && <div className="text-red-500 text-sm mt-3 text-center p-2 bg-red-50 rounded-md">{error}</div>}
        {loading && <div className="text-orange-500 text-sm mt-3 text-center">Processing...</div>}
      </div>
    </div>
  );
};

export default ResetPassword;
